import { useMemo } from "react";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import PropTypes from "prop-types";
import moment from "moment";
import { ErrorMessage } from "formik";
import {
  commonInputStyles,
  labelStyles
} from "components/form_new/component-styles";

const DateInputField = ({ label, field, form, ...rest }) => {
  const errorId = `${field.name}-error`;
  const hasError = form.errors[field.name] && form.touched[field.name];

  // Return the SAME Moment reference MUI last emitted, so its internal field
  // state can rely on reference equality. Re-hydrating a string into a fresh
  // Moment on every render triggers an update loop inside x-date-pickers v6
  // (`useFieldState` / `useValueWithTimezone`) that crashes the form as soon
  // as the user types in a section (S3DEV2-7559). For legacy string values
  // coming from initial data we still coerce to a Moment for display.
  const value = useMemo(() => {
    if (!field.value) return null;
    if (moment.isMoment(field.value)) return field.value;
    const momentValue = moment(field.value);
    return momentValue.isValid() ? momentValue : null;
  }, [field.value]);

  return (
    <div style={{ width: "100%", margin: 0 }}>
      <span
        id={errorId}
        className="form-field-error"
        role="alert"
        aria-live="polite"
        style={{ display: hasError ? "block" : "none", color: "red", fontSize: "0.875rem", marginTop: "4px" }}
      >
        <ErrorMessage name={field.name} />
      </span>

      <LocalizationProvider dateAdapter={AdapterMoment}>
        <DesktopDatePicker
          label={label}
          name={field.name}
          value={value}
          onChange={value => {
            if (value && value.isValid && value.isValid()) {
              // Store the exact Moment MUI emitted (see the `useMemo` above).
              // Override `toJSON` per-instance so JSON.stringify emits a
              // timezone-independent YYYY-MM-DD string on the wire, keeping
              // the S3DEV2-7521 fix without swapping the stored type.
              value.toJSON = function toDateOnlyJson() {
                return this.format("YYYY-MM-DD");
              };
              form.setFieldValue(field.name, value);
            } else {
              form.setFieldValue(field.name, undefined);
            }
          }}
          onClose={() => {
            // Mark field as touched when date picker closes (after user interaction)
            form.setFieldTouched(field.name, true);
          }}
          slotProps={{
            textField: {
              sx: {
                ...commonInputStyles,
                width: "100%"
              },
              InputLabelProps: {
                sx: labelStyles,
                shrink: true
              },
              error: hasError,
              onBlur: () => form.setFieldTouched(field.name, true),
              inputProps: {
                'aria-describedby': hasError ? errorId : undefined,
                'aria-invalid': hasError
              }
            },
            field: {
              clearable: true,
              onClear: () => form.setFieldValue(field.name, undefined)
            },
            popper: {
              sx: {
                '& .MuiPickersCalendarHeader-label': {
                  overflow: 'visible',
                },
                '& .MuiDayCalendar-weekDayLabel': {
                  minWidth: '40px',
                  width: 'auto',
                },
                '& .MuiPickersDay-root': {
                  minWidth: '40px',
                  minHeight: '40px',
                  width: 'auto',
                  height: 'auto',
                },
              }
            },
          }}
          {...rest}
        />
      </LocalizationProvider>
    </div>
  );
};

DateInputField.propTypes = {
  label: PropTypes.string.isRequired,
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    onBlur: PropTypes.func
  }),
  form: PropTypes.shape({
    setFieldValue: PropTypes.func,
    setFieldTouched: PropTypes.func,
    errors: PropTypes.object,
    touched: PropTypes.object
  }).isRequired
};

export default DateInputField;
